// Copyright 2018 Sorama B.V.

import { autoinject, bindable, BindingEngine, TaskQueue } from "aurelia-framework";
import { AudioPlayer } from "controls/audioplayer/AudioPlayer";
import { Point3D, TimeSignalAnalysisProperties } from "datatypes/SoramaLocalApi";
import { GenericMeasurement, MeasurementPartType } from "datatypes/SoramaWebApi";
import { MeasurementExtensions } from "extensions/MeasurementExtensions";
import { MediaQueries } from "libraries/MediaQueries/MediaQueries";
import { AnnotatedTimeData, TimeDataSet } from "modules/modals/TimeSelection/TimeSelectionModal";
import { MeasurementRepository } from "repositories/MeasurementRepository";
import { IMeasurementProcessorResolver } from "services/Measurement/IMeasurementProcessorResolver";
import { IPlayablePropertiesService } from "services/PlayableProperties/IPlayablePropertiesService";
import { ISpectralProcessingService } from "services/SoundProcessing/ISpectralProcessingService";

/**
 * Adds the methods used to calculate the (multiple) audiostreams and handles controls for the playable settings.
 *
 **/
@autoinject
export class AudioStreamsPlayer extends AudioPlayer {
    private measurementRepository: MeasurementRepository;
    private processorResolver: IMeasurementProcessorResolver;

    private currentTimeSignals: TimeDataSet[] = [];
    private loadedStreams: { id: string; url: string }[] = [];

    @bindable
    public audioSelected: boolean;

    @bindable
    private selectedTrack: string;

    @bindable
    public changeInMeasurement: boolean;

    @bindable
    private playbackRate: number;

    public busy: boolean;
    private busyPromise: Promise<void>;

    constructor(
        bindingEngine: BindingEngine,
        playablePropertiesService: IPlayablePropertiesService,
        mediaQueries: MediaQueries,
        taskQueue: TaskQueue,
        measurementRepository: MeasurementRepository,
        processorResolver: IMeasurementProcessorResolver
    ) {
        super(bindingEngine, playablePropertiesService, mediaQueries, taskQueue);

        this.measurementRepository = measurementRepository;
        this.processorResolver = processorResolver;
    }

    public updateAudio(selectedMeasurementId: string): void {
        if (!this.selectedTrack || this.selectedTrack == "") {
            return;
        }

        const promise = this.busyPromise ? this.busyPromise : Promise.resolve();

        //Queue after existing promise
        this.busyPromise = promise.then(() => {
            this.busy = true;

            if (this.playablePropertiesService.playableProperties[this.playableId].isPlaying) {
                this.stopPlaying();
                this.playablePropertiesService.pause(this.playableId);
            }

            this.data = [];
            this.audioElementSource = undefined;
            this.audioElement = undefined;

            const newSignals: TimeDataSet[] = [];

            const measurementData = this.measurementRepository.getMeasurement(selectedMeasurementId);

            measurementData
                .then((x) => {
                    const numberOfChannels = MeasurementExtensions.getNumberOfChannels(x);

                    return <TimeDataSet>{
                        measurement: x,
                        selectedChannels: Array(numberOfChannels).fill(false),
                        timeDatas: Array(numberOfChannels).fill(undefined),
                    };
                })
                .then((timeSet) => {
                    const micPositions = MeasurementExtensions.getMicrophonePositionsForMeasurement(
                        timeSet.measurement
                    );

                    const timePromises: Promise<void>[] = [];

                    // Check if data is already available
                    if (!timeSet.timeDatas[0]) {
                        timePromises.push(
                            this.getTimeData(timeSet.measurement, micPositions[0]).then((x) => {
                                timeSet.timeDatas[0] = x;
                            })
                        );
                    }

                    return Promise.all(timePromises).then(() => {
                        newSignals.push(timeSet);

                        this.currentTimeSignals.splice(0, this.currentTimeSignals.length, ...newSignals);

                        if (this.currentTimeSignals.length > 0) {
                            this.data = this.currentTimeSignals[0].timeDatas[0].dataPoints;
                            this.currentTimeSignals[0].measurement.parts
                                .filter((x) => x.measurementType === MeasurementPartType.Audio)
                                .map((x) => {
                                    this.samplerate = x.device.sampleRate;
                                });
                        }

                        this.busy = false;
                        this.dataChanged().then(() => {
                            if (!this.loadedStreams.find((x) => x.id == selectedMeasurementId)) {
                                this.loadedStreams.push({ id: selectedMeasurementId, url: this.audioElementSource });
                            }
                        });
                    });
                });
        });
    }

    private getTimeData(measurement: GenericMeasurement, mic: Point3D): Promise<AnnotatedTimeData> {
        return this.processorResolver
            .resolveProcessor<ISpectralProcessingService>(ISpectralProcessingService, measurement)
            .then((service) => {
                return service
                    .getTimeSignals(measurement, <TimeSignalAnalysisProperties>{
                        requestedMicrophone: mic,
                        properties: [
                            {
                                timeRange: { from: 0, to: measurement.duration },
                            },
                        ],
                    })
                    .then((x) => {
                        let min = Number.MAX_VALUE;
                        let max = Number.MIN_VALUE;

                        for (let i = 0; i < x[0].data.length; i++) {
                            const sample = x[0].data[i];
                            min = Math.min(sample.y, min);
                            max = Math.max(sample.y, max);
                        }

                        const subIdx = MeasurementExtensions.micToSubIndexString(measurement, mic);

                        return <AnnotatedTimeData>{
                            measurement: measurement,
                            dataPoints: x[0].data,
                            name: MeasurementExtensions.getDisplaySubIndexDisplayName(measurement, subIdx),
                            minVal: min,
                            maxVal: max,
                            subIdx: subIdx,
                        };
                    });
            });
    }

    //Change Handlers
    private selectedTrackChanged(): void {
        if (this.loadedStreams.find((x) => x.id == this.selectedTrack)) {
            if (this.playablePropertiesService.playableProperties[this.playableId].isPlaying) {
                this.stopPlaying();
            }

            this.loadedStreams.forEach((x) => {
                if (x.id == this.selectedTrack) {
                    this.audioElementSource = x.url;
                }
            });
            this.audioElement.load();

            return;
        }

        if (!this.selectedTrack && this.audioElement) {
            this.audioElement.load();
        } else {
            this.updateAudio(this.selectedTrack);
        }
    }

    private changeInMeasurementChanged(): void {
        this.audioSelected = false;
        this.selectedTrack = "";
        this.playablePropertiesService.pause(this.playableId);
    }

    private playbackRateChanged(): void {
        if (!this.audioElement) {
            return;
        }

        if (this.playbackRate != 1) {
            this.changeInMeasurementChanged();
        } else {
            this.selectedTrackChanged();
        }
    }
}
